import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Shield, ChartBar as BarChart3, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trackPageView } from '../hooks/useSiteAnalytics';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

const COOKIE_CONSENT_KEY = 'cybertronic_cookie_consent';

export default function CookieConsent() {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setShowBanner(true);
    } else {
      initializeAnalytics(consent === 'accepted');
    }
  }, []);

  const initializeAnalytics = (accepted: boolean) => {
    const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;

    if (accepted && measurementId && !window.gtag) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag(...args: any[]) {
        window.dataLayer?.push(args);
      };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        anonymize_ip: true,
        cookie_flags: 'SameSite=None;Secure'
      });
    }
  };

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    initializeAnalytics(true);
    // Fire an initial page-view ping now that analytics is consented
    trackPageView(window.location.pathname + window.location.search);
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    setShowBanner(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <>
          {/* Blocking overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9998]"
            style={{ pointerEvents: 'all' }}
          />

          {/* Cookie banner */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none"
          >
          <div className="w-full max-w-2xl pointer-events-auto">
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl border border-cyan-500/30 overflow-hidden">
              <div className="relative p-6 md:p-8">
                {/* Header with icon */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-cyan-500/20 rounded-xl p-3 flex-shrink-0">
                    <Cookie className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                      {t('cookies.title', 'Cookies & Privacy')}
                    </h3>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      {t('cookies.description', 'We use cookies to enhance your experience, analyze site traffic, and for marketing purposes. Your data is processed securely and anonymously.')}
                    </p>
                  </div>
                </div>

                {/* Details section */}
                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mb-4 overflow-hidden"
                    >
                      <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                        <div className="flex items-start gap-3">
                          <Shield className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="text-white font-semibold text-sm mb-1">
                              {t('cookies.essential', 'Essential Cookies')}
                            </h4>
                            <p className="text-slate-400 text-xs">
                              {t('cookies.essentialDesc', 'Required for basic site functionality. Always active.')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <BarChart3 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="text-white font-semibold text-sm mb-1">
                              {t('cookies.analytics', 'Analytics Cookies')}
                            </h4>
                            <p className="text-slate-400 text-xs">
                              {t('cookies.analyticsDesc', 'Help us understand how visitors interact with our website (Google Analytics).')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-300 hover:text-white transition-all duration-200 text-sm font-medium group border border-slate-600/50"
                  >
                    <Settings className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
                    {showDetails ? t('cookies.hideDetails', 'Hide Details') : t('cookies.showDetails', 'Details')}
                  </button>

                  <button
                    onClick={handleDecline}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-700/50 hover:bg-red-600/20 text-slate-300 hover:text-red-400 transition-all duration-200 text-sm font-medium group border border-slate-600/50 hover:border-red-500/50"
                  >
                    <X className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
                    {t('cookies.decline', 'Decline')}
                  </button>

                  <button
                    onClick={handleAccept}
                    className="flex-1 sm:flex-none px-6 py-2.5 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold text-sm transition-all duration-200 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-105"
                  >
                    {t('cookies.accept', 'Accept All Cookies')}
                  </button>
                </div>

                {/* Footer note */}
                <p className="text-xs text-slate-500 mt-4 text-center">
                  {t('cookies.footer', 'By accepting, you consent to our use of cookies as described in our Privacy Policy.')}
                </p>
              </div>
            </div>
          </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
